import { useCallback } from 'react';
import {
	type UIAnalyticsEvent,
	fireScreenAnalytics,
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';

import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import {
	ENTITY_ID_TO_TARGET_TYPE,
	EXPERIMENT_ID,
	type CohortType,
	type RecommendationEntityIdType,
} from '../constants';

export interface UseEventProps {
	cohort: CohortType | string;
	sessionId: string;
}

export interface FireClickEventProps {
	entityId?: RecommendationEntityIdType;
	eventName: string;
}
export interface FireUIAnalyticsEventProps {
	entityId: RecommendationEntityIdType;
	analyticsEvent: UIAnalyticsEvent;
}
export interface FireViewEventProps {
	entityId?: RecommendationEntityIdType;
}

export function useExposureEvent({ cohort, sessionId }: UseEventProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isSiteAdmin } = useTenantContext();

	const fireExposureEvent = useCallback(
		({ entityId }: { entityId: RecommendationEntityIdType }) => {
			const targetProduct = ENTITY_ID_TO_TARGET_TYPE[entityId];

			fireTrackAnalytics(
				createAnalyticsEvent({
					actionSubject: 'feature',
					action: 'exposed',
				}),
				'feature exposed',
				{
					growthInitiative: 'cross-flow',
					cohort,
					flagKey: EXPERIMENT_ID,
					recommendationSessionId: sessionId,
					targetProduct,
					isSiteAdmin,
				},
			);
		},
		[cohort, sessionId, isSiteAdmin, createAnalyticsEvent],
	);

	return { fireExposureEvent };
}

export function useCustomAnalyticEvents({ cohort, sessionId }: UseEventProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isSiteAdmin } = useTenantContext();

	const fireClickEvent = useCallback(
		({ eventName, entityId }: FireClickEventProps) => {
			const targetProduct = entityId ? ENTITY_ID_TO_TARGET_TYPE[entityId] : undefined;

			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				eventName,
				{
					growthInitiative: 'cross-flow',
					flagKey: EXPERIMENT_ID,
					cohort,
					recommendationSessionId: sessionId,
					targetProduct,
					targetProductId: entityId,
					isSiteAdmin,
				},
			);
		},
		[cohort, sessionId, isSiteAdmin, createAnalyticsEvent],
	);

	const fireUIAnalyticsEvent = useCallback(
		({ analyticsEvent, entityId }: FireUIAnalyticsEventProps) => {
			const targetProduct = ENTITY_ID_TO_TARGET_TYPE[entityId];

			fireUIAnalytics(analyticsEvent, analyticsEvent.payload.actionSubjectId, {
				growthInitiative: 'cross-flow',
				flagKey: EXPERIMENT_ID,
				cohort,
				recommendationSessionId: sessionId,
				targetProduct,
				targetProductId: entityId,
				isSiteAdmin,
			});
		},
		[cohort, sessionId, isSiteAdmin],
	);

	const fireViewedEvent = useCallback(
		({ entityId }: FireViewEventProps) => {
			const targetProduct = entityId ? ENTITY_ID_TO_TARGET_TYPE[entityId] : undefined;

			fireScreenAnalytics(createAnalyticsEvent({}), {
				cohort,
				growthInitiative: 'cross-flow',
				flagKey: EXPERIMENT_ID,
				recommendationSessionId: sessionId,
				targetProduct,
				targetProductId: entityId,
				isSiteAdmin,
				recommendedProductId: entityId,
			});
		},
		[cohort, sessionId, isSiteAdmin, createAnalyticsEvent],
	);

	return { fireClickEvent, fireUIAnalyticsEvent, fireViewedEvent };
}
